import React, { useState, useEffect, useRef } from 'react';
import { 
  IonContent, 
  IonHeader, 
  IonPage, 
  IonTitle, 
  IonToolbar, 
  IonButtons, 
  IonBackButton,
  IonButton,
  IonIcon,
  IonInput,
  IonTextarea,
  IonItem,
  IonLabel,
  IonRange,
  useIonToast,
  useIonAlert,
  IonSpinner,
  IonBadge,
  IonFooter,
  IonModal,
  IonSearchbar,
  IonChip,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonList
} from '@ionic/react';
import { trash, save, mic, stop, play, pause, close, checkmark, shareOutline, personAddOutline, closeCircle } from 'ionicons/icons';
import { useParams, useHistory } from 'react-router-dom';
import getApi from '../api';
import { Note, User } from './Notes';

const NoteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const api = getApi();
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [audioUrl, setAudioUrl] = useState<string | undefined>(undefined);
  const [transcriptionStatus, setTranscriptionStatus] = useState<string>('none');
  
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isOwner, setIsOwner] = useState(true);
  const [sharedWith, setSharedWith] = useState<User[]>([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const analyserRef = useRef<AnalyserNode | null>(null);
  const timerIntervalRef = useRef<any>(null);
  const shouldSaveRef = useRef(false);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (id !== 'new') {
      fetchNoteDetail();
    }
  }, [id]);

  const fetchAvailableUsers = async () => {
    try {
      const response = await api.get('/api/user/');
      const users = response.data.results || response.data;
      setAvailableUsers(users);
      setFilteredUsers(users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      presentToast({ message: 'Kunde inte hämta användare', color: 'danger', duration: 2000 });
    }
  };

  const handleOpenShareModal = () => {
    fetchAvailableUsers();
    setShowShareModal(true);
  };

  const handleShareWith = async (user: User) => {
    try {
      await api.post(`/api/user/notes/${id}/share/`, { user_ids: [user.id] });
      setSharedWith([...sharedWith, user]);
      presentToast({ message: `Delad med ${user.name}`, color: 'success', duration: 2000 });
    } catch (error) {
      console.error('Failed to share note:', error);
      presentToast({ message: 'Kunde inte dela anteckning', color: 'danger', duration: 2000 });
    }
  };

  const handleUnshare = async (user: User) => {
    try {
      await api.post(`/api/user/notes/${id}/unshare/`, { user_ids: [user.id] });
      setSharedWith(sharedWith.filter(u => u.id !== user.id));
      presentToast({ message: `Delning borttagen för ${user.name}`, color: 'success', duration: 2000 });
    } catch (error) {
      console.error('Failed to unshare note:', error);
      presentToast({ message: 'Kunde inte ta bort delning', color: 'danger', duration: 2000 });
    }
  };

  useEffect(() => {
    if (userSearch.trim() === '') {
      setFilteredUsers(availableUsers);
    } else {
      const search = userSearch.toLowerCase();
      setFilteredUsers(
        availableUsers.filter(user => 
          user.name.toLowerCase().includes(search) ||
          user.email.toLowerCase().includes(search)
        )
      );
    }
  }, [userSearch, availableUsers]);

  const fetchNoteDetail = async () => {
    try {
      const response = await api.get<Note>(`/api/user/notes/${id}/`);
      const note = response.data;
      setTitle(note.title);
      setContent(note.content);
      setAudioUrl(note.audio_url);
      setTranscriptionStatus(note.transcription_status);
      setIsOwner(note.is_owner !== false);
      setSharedWith(note.shared_with || []);
    } catch (error) {
      console.error('Failed to fetch note:', error);
      presentToast({ message: 'Kunde inte hämta anteckning', color: 'danger', duration: 3000 });
    }
  };

  // Setup audio player when audioUrl changes
  useEffect(() => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audioPlayerRef.current = audio;
      
      // Reset state
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);

      const updateTime = () => setCurrentTime(audio.currentTime);
      const updateDuration = () => {
        if (audio.duration && !isNaN(audio.duration) && audio.duration !== Infinity) {
          setDuration(audio.duration);
        }
      };
      const onEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      audio.addEventListener('timeupdate', updateTime);
      audio.addEventListener('loadedmetadata', updateDuration);
      audio.addEventListener('ended', onEnded);
      
      // Force load to get metadata
      audio.load();

      return () => {
        audio.pause();
        audio.removeEventListener('timeupdate', updateTime);
        audio.removeEventListener('loadedmetadata', updateDuration);
        audio.removeEventListener('ended', onEnded);
        audioPlayerRef.current = null;
      };
    } else {
      setDuration(0);
      setCurrentTime(0);
    }
  }, [audioUrl]);

  const handleSave = async (blobToSave?: Blob) => {
    // Capture current state
    const blob = blobToSave || audioBlob;
    const currentTitle = title;
    const currentContent = content;
    
    // Navigate back immediately for better UX
    history.goBack();
    presentToast({
        message: 'Sparar...',
        duration: 1000,
        color: 'medium'
    });

    try {
      if (id === 'new') {
        // Create new note
        const formData = new FormData();
        formData.append('title', currentTitle);
        formData.append('content', currentContent);
        if (blob) {
          formData.append('audio_file', blob, 'recording.webm');
        }

        await api.post('/api/user/notes/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Update existing note
        if (blob) {
             const formData = new FormData();
             formData.append('title', currentTitle);
             formData.append('content', currentContent);
             formData.append('audio_file', blob, 'recording.webm');
             await api.patch(`/api/user/notes/${id}/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
             });
        } else {
            // JSON update for text only
            await api.patch(`/api/user/notes/${id}/`, {
                title: currentTitle,
                content: currentContent
            });
        }
      }

      presentToast({
        message: 'Anteckning sparad',
        duration: 2000,
        color: 'success'
      });
    } catch (error) {
      console.error('Failed to save note:', error);
      presentToast({
        message: 'Kunde inte spara anteckning',
        duration: 3000,
        color: 'danger'
      });
    }
  };

  const handleDelete = () => {
    presentAlert({
      header: 'Radera anteckning',
      message: 'Är du säker på att du vill radera denna anteckning?',
      buttons: [
        { text: 'Avbryt', role: 'cancel' },
        {
          text: 'Radera',
          role: 'destructive',
          handler: async () => {
            try {
                await api.delete(`/api/user/notes/${id}/`);
                history.goBack();
            } catch (error) {
                console.error('Failed to delete note:', error);
                presentToast({ message: 'Kunde inte radera anteckning', color: 'danger', duration: 3000 });
            }
          }
        }
      ]
    });
  };

  const drawVisualizer = () => {
    if (!analyserRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext('2d');
    if (!canvasCtx) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);
      analyserRef.current!.getByteFrequencyData(dataArray);

      canvasCtx.fillStyle = 'white';
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / bufferLength) * 2.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        barHeight = dataArray[i] / 1.5;
        canvasCtx.fillStyle = '#4F933E';
        
        const y = (canvas.height - barHeight) / 2;
        canvasCtx.fillRect(x, y, barWidth, barHeight);
        
        x += barWidth + 1;
      }
    };
    draw();
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 64;
      source.connect(analyser);
      analyserRef.current = analyser;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        audioContext.close();

        if (shouldSaveRef.current) {
            handleSave(blob);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setIsPaused(false);
      setRecordingDuration(0);
      
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

      setTimeout(drawVisualizer, 100);

    } catch (err) {
      console.error('Error accessing microphone:', err);
      presentToast({
        message: 'Kunde inte starta inspelning. Kontrollera behörigheter.',
        duration: 3000,
        color: 'danger'
      });
    }
  };

  const stopRecording = (save: boolean) => {
    if (mediaRecorderRef.current && isRecording) {
      shouldSaveRef.current = save;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    }
  };

  const togglePauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
        if (isPaused) {
            mediaRecorderRef.current.resume();
            setIsPaused(false);
            timerIntervalRef.current = setInterval(() => {
                setRecordingDuration(prev => prev + 1);
            }, 1000);
        } else {
            mediaRecorderRef.current.pause();
            setIsPaused(true);
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        }
    }
  };

  const togglePlayback = () => {
    if (audioPlayerRef.current) {
      if (isPlaying) {
        audioPlayerRef.current.pause();
        setIsPlaying(false);
      } else {
        audioPlayerRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleSeek = (e: CustomEvent) => {
    if (audioPlayerRef.current) {
      const newTime = e.detail.value as number;
      audioPlayerRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
      <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonBackButton defaultHref="/notes" />
          </IonButtons>
          <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{id === 'new' ? 'Ny anteckning' : 'Redigera anteckning'}</IonTitle>
          <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {id !== 'new' && isOwner && (
              <IonButton onClick={handleOpenShareModal} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonIcon slot="icon-only" icon={shareOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              </IonButton>
            )}
            {id !== 'new' && (
              <IonButton onClick={handleDelete} disabled={!isOwner} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonIcon slot="icon-only" icon={trash} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              </IonButton>
            )}
            <IonButton onClick={() => handleSave()} disabled={isSaving} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              {isSaving ? <IonSpinner name="crescent" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} /> : <IonIcon slot="icon-only" icon={save} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        {isRecording ? (
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'white',
                zIndex: 1000
            }}>
                <canvas ref={canvasRef} width="300" height="100" style={{ width: '100%', height: '150px', marginBottom: '20px' }} />
                
                <div style={{ fontSize: '32px', color: 'var(--ion-color-primary)', fontWeight: 'bold', marginBottom: '40px' }}>
                    {formatTime(recordingDuration)}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <IonButton 
                        fill="clear" 
                        onClick={() => stopRecording(false)}
                        color="medium"
                        placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                    >
                        Avbryt
                    </IonButton>

                    <IonButton 
                        onClick={togglePauseRecording}
                        style={{ 
                            width: '72px', 
                            height: '72px', 
                            '--border-radius': '50%',
                            '--background': '#eb445a',
                            '--box-shadow': '0 4px 10px rgba(235, 68, 90, 0.3)'
                        }}
                        placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                    >
                        <IonIcon icon={isPaused ? mic : pause} style={{ fontSize: '32px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                    </IonButton>

                    <IonButton 
                        fill="clear" 
                        onClick={() => stopRecording(true)}
                        color="primary"
                        placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                    >
                        Spara
                    </IonButton>
                </div>
                <div style={{ marginTop: '20px', color: '#999', fontSize: '14px' }}>
                    {isPaused ? 'Pausad' : 'Spelar in...'}
                </div>
            </div>
        ) : (
        <>
        {transcriptionStatus !== 'none' && transcriptionStatus !== 'completed' && (
            <div style={{ padding: '10px', textAlign: 'center' }}>
                <IonBadge color={transcriptionStatus === 'failed' ? 'danger' : 'warning'} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    {transcriptionStatus === 'pending' && 'Väntar på transkribering...'}
                    {transcriptionStatus === 'processing' && 'Transkribering pågår...'}
                    {transcriptionStatus === 'failed' && 'Transkribering misslyckades'}
                </IonBadge>
            </div>
        )}

        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Titel</IonLabel>
          <IonInput 
            value={title} 
            placeholder="Ange titel" 
            onIonChange={e => setTitle(e.detail.value!)} 
            onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
          />
        </IonItem>

        <IonItem lines="none" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Innehåll</IonLabel>
          <IonTextarea 
            value={content} 
            placeholder="Skriv din anteckning här..." 
            rows={15} 
            onIonChange={e => setContent(e.detail.value!)} 
            onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
          />
        </IonItem>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          {audioUrl ? (
            <div style={{ width: '100%', padding: '0 10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <IonButton onClick={togglePlayback} fill="clear" size="large" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonIcon icon={isPlaying ? pause : play} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                </IonButton>
                <div style={{ flex: 1, margin: '0 10px' }}>
                  <IonRange 
                    min={0} 
                    max={duration} 
                    value={currentTime} 
                    onIonChange={handleSeek}
                    placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                  ></IonRange>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginTop: '-5px' }}>
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
                <IonButton onClick={() => { setAudioUrl(undefined); setAudioBlob(null); }} color="danger" fill="clear" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonIcon icon={trash} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                </IonButton>
              </div>
            </div>
          ) : (
            <IonButton 
              onClick={startRecording} 
              color="primary"
              shape="round"
              placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
            >
              <IonIcon slot="start" icon={mic} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              Spela in ljud
            </IonButton>
          )}
        </div>
        </>
        )}
      </IonContent>

      <IonModal isOpen={showShareModal} onDidDismiss={() => setShowShareModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Dela anteckning</IonTitle>
            <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonButton onClick={() => setShowShareModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonIcon icon={close} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
          <IonToolbar placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonSearchbar
              value={userSearch}
              onIonChange={e => setUserSearch(e.detail.value!)}
              placeholder="Sök användare..."
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          {sharedWith.length > 0 && (
            <IonCard placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonCardHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonCardTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Delad med ({sharedWith.length})</IonCardTitle>
              </IonCardHeader>
              <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                {sharedWith.map(user => (
                  <IonChip key={user.id} color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>{user.name}</IonLabel>
                    <IonIcon icon={closeCircle} onClick={() => handleUnshare(user)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                  </IonChip>
                ))}
              </IonCardContent>
            </IonCard>
          )}

          <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            {filteredUsers
              .filter(user => !sharedWith.some(u => u.id === user.id))
              .map(user => (
                <IonItem key={user.id} button onClick={() => handleShareWith(user)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                  <IonIcon slot="start" icon={personAddOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                  <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <h2>{user.name}</h2>
                    <p>{user.email}</p>
                    {user.company_name && <p style={{ fontSize: '0.85em', color: '#666' }}>{user.company_name}</p>}
                  </IonLabel>
                </IonItem>
              ))}
          </IonList>
        </IonContent>
      </IonModal>
    </IonPage>
  );
};

export default NoteDetail;
